import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { RequestBwt } from "../../shared/models/request/request-bwt";
import { RequestsService } from "../../shared/services/requests.service";
import { ActivatedRoute, Router } from "@angular/router";
import { AppService } from "../../app.service";
import { NotificationService } from "../../core/services/notification.service";
import { NgbTabChangeEvent } from "@ng-bootstrap/ng-bootstrap";
import { EntityViewModel } from "../../shared/models/core/EntityViewModel";
import { TrackChangesComponent } from "../../core/interfaces/component-can-deactivate";
import { SecurityService } from "../../core/services/security.service";
import { Activity } from "../../shared/models/activity/activity";
import { ActivityStatusEnum, RequestType } from "../../shared/enums";
import { ActivitiesService } from "../../shared/services/activities.service";
import { KeyValueObject } from "../../shared/models/core/KeyValueObject";
import { LookupService } from "../../shared/services/lookup.service";
import { RequestPageMainComponent } from "../../shared/components/request-page/request-page-main/request-page-main.component";
import { CommentsBlockComponent } from "../../shared/components/comments/comments-block/comments-block.component";
import { NotificationType } from "../../core/services/notification-type";
import { environment } from "../../../environments/environment";
import { UsersService } from "../../admin/users/users.service";
import { formatDate } from '@telerik/kendo-intl';

import '@progress/kendo-ui';
import { RequestStatusWorkflowTransitionsComponent } from "../../shared/components/request-page/request-page-main/request-status-workflow-transitions/request-status-workflow-transitions.component";
import { RequestChangeStatusResult } from "../../shared/models/request/request-change-status-result";
import { RequestPrintFormsService } from "../../shared/services/request-print-forms.service";
import { forkJoin } from "rxjs";
import { DropDownListComponent } from "@progress/kendo-angular-dropdowns";
import { SignalRNotificationsService } from "../../shared/services/signalr-notifications.service";
import { RequestPageActivitiesComponent } from "../../shared/components/request-page/request-page-activities/request-page-activities.component";
import { RequestTypeKind } from "../../shared/enums/request-type-kind.enum";
import { RequestRelatedTree } from "../../shared/models/request/request-related-tree";

declare var kendo: any;

@Component({
	selector: 'request',
	templateUrl: './request.page.html',
	styleUrls: [
		'./request.page.scss',
		'../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
		'../../../vendor/libs/angular2-ladda/angular2-ladda.scss',
		'../../../vendor/libs/ngx-contextmenu/ngx-contextmenu.scss',
	]
})
export class RequestPage implements OnInit, AfterViewInit, OnDestroy, TrackChangesComponent {
  
	@ViewChild("requestPageMain") requestPageMainComponent: RequestPageMainComponent;
	@ViewChild("commentBlock") commentBlock: CommentsBlockComponent;
	@ViewChild('requestStatusWorkflowTransitionsComponent') workflowTransitionsComponent: RequestStatusWorkflowTransitionsComponent;
	@ViewChild('requestPrintFormsDropdown') requestPrintFormsDropdown: DropDownListComponent;
	@ViewChild('requestPageActivities') requestPageActivities: RequestPageActivitiesComponent;
	@ViewChild("requestPageMain", { read: ElementRef }) requestPageMainElement: ElementRef;

	private entityId: string;
	private entityIdInt: number;

	private changeRequestStatusMessageListener: string = "changeRequestStatus";
	public activeIdString: string = "main";

	public entityViewModel: EntityViewModel<RequestBwt>;

	requestPrintFormId: number;
	showActivitiesGrid: boolean = true;
	showActivityDetails: boolean = true;
	selectedActivities: number[] = [];
	activities: Activity[] = [];
	selectedActivity: Activity = null;
	workflowTransitions: KeyValueObject[] = [];
	requestStatuses: KeyValueObject[] = [];
	requestPrintForms: KeyValueObject[] = [];

	requestStatusBarLoading: boolean = false;
	dataSaving: boolean = false;
	destroy: boolean = false;
	updateFromCurrentClient: boolean;
	/** Старое поведение чата без блока связанных заявок. */
	minRequestCommentsHeight: number = 1000;
	defaultRequestCommentsHeight: number = null;
	relatedTree: RequestRelatedTree = null;
	relatedBlockVisible: boolean = false;
	relatedTopMarginPx: number = 0;
	private commentsHeightTick: number = 0;

	constructor(
		public requestsService: RequestsService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected appService: AppService,
		protected notificationService: NotificationService,
		private securityService: SecurityService,
		private activitiesService: ActivitiesService,
		private lookupService: LookupService,
		private usersService: UsersService,
		private requestPrintFormsService: RequestPrintFormsService,
		private signalR: SignalRNotificationsService,
		private changeDetector: ChangeDetectorRef,
		private ngZone: NgZone
	) {
		this.appService.pageTitle = 'Заявка';	
	}

    ngOnInit(): void {
		this.route.fragment.subscribe(x => {
			if (x) {
				this.activeIdString = (x === 'services' || x === 'requestDevices')
					? 'servicesAndDevices'
					: x;
			}
		});

		this.entityId = this.route.snapshot.paramMap.get('requestId');
		this.entityIdInt = Number(this.entityId);

		const requestStatuses$ = this.lookupService.getData("request-statuses", null);
		const requestPrintForms$ = this.requestPrintFormsService.getRequestPrintForms(this.entityIdInt);
		const requestBwt$ = this.requestsService.getBwtById(this.entityId);

		forkJoin([requestStatuses$, requestPrintForms$, requestBwt$])
		.subscribe(([requestStatuses, requestPrintForms, requestBwt]) => {
			this.requestStatuses = requestStatuses;
			this.requestPrintForms = requestPrintForms;

			this.entityViewModel = requestBwt;
			this.checkPerformerUser();
			this.formatWorkflowTransitions();
			this.loadRelatedTree(this.entityIdInt);
			if (this.isEngineerRole) {
				this.activeIdString = "activities";
			}
		});
    }

	ngAfterViewInit(): void {
		this.initSignalR(this.entityId);
		setTimeout(() => this.syncRelatedLayout(), 0);
	}

	formatWorkflowTransitions() {
		this.workflowTransitions = this.entityViewModel.entity.workflowTransitions.map(x => {
			return new KeyValueObject(x.transitionId, x.transitionName);
		});
		this.workflowTransitions.unshift(new KeyValueObject(this.entityViewModel.entity.requestStatusId, this.entityViewModel.entity.requestStatusName));
	}

	private initSignalR(entityId: string) {
		this.signalR.startConnection().subscribe(() => {
			this.signalR.addToGroup(entityId);
			this.signalR.getOrCreateMessageListener(this.changeRequestStatusMessageListener).subscribe(data => {
				this.ngZone.run(() => {
					console.log(data);
					console.log(this.updateFromCurrentClient);
					if (!this.updateFromCurrentClient) {
						this.requestsService.getBwtById(this.entityIdInt).subscribe(updatedRequest => {
							this.refreshRequest(updatedRequest.entity);
						});
					}
					else {
						this.updateFromCurrentClient = false;
					}
				});
			});
		});
	}

	checkPerformerUser() {
		if (!this.entityViewModel.entity.performerUserId && this.entityViewModel.entity.performerUserAutoFill) {
			setTimeout(() => 
				this.setCurrentPerformerUser(),
				environment.performerUserAutoSetTime
			);
		}
	}

	setCurrentPerformerUser() {
		if (!this.entityViewModel.entity.performerUserId && !this.destroy) {
			this.dataSaving = true;
			this.requestsService.changePerformerUser(this.entityIdInt, null, true).subscribe((resp) => {
				if (resp.data != null) {
					this.usersService.getUsersByGroup(this.entityViewModel.entity.performerUserGroupId).subscribe(users => {
						this.entityViewModel.entity.performerUserId = resp.data;
						this.entityViewModel.entity.performerUserName = users.find(x => x.id == resp.data).name;

						if (this.commentBlock) {
							this.commentBlock.loadComments(0, null);
						}

						this.dataSaving = false;
						this.notificationService.success({
							title: "Вы назначены исполнителем по заявке",
							notificationType: NotificationType.Toast
						});
					});
				}
				this.dataSaving = false;
			});
		}
	}

	tabChanged(e: NgbTabChangeEvent) {
		this.activeIdString = e.nextId;
	}
	
	showActivityById(activityId: number) {
		if (this.isMobileDevice) {
			this.showActivitiesGrid = false;
			this.showActivityDetails = true;
		}

		this.selectedActivities = [];
		let activity = this.activities.find(x => x.activityId == activityId);
		if (!this.statusIsCanceled(activity)){
			this.selectedActivities.push(activityId);
		}
		this.onSelectedActivityChange();
	}

	goToActivity(activityId: number) {
		this.activeIdString = "activities-tab";
		this.showActivityById(activityId);
	}

	refreshRequest(request: RequestBwt) {
		this.entityViewModel.entity = request;
		this.loadRelatedTree(request.requestId);
		if (this.requestPageMainComponent) {
			this.requestPageMainComponent.request = request;
			this.requestPageMainComponent.initData();
			this.requestPageMainComponent.prepareRequestData();
			this.requestPageMainComponent.loadContragent();
		}

		this.requestPrintFormsService.getRequestPrintForms(this.entityIdInt).subscribe(requestPrintForms => {
			this.requestPrintForms = requestPrintForms;
		})

		this.workflowTransitionsComponent.requestStatusId = request.requestStatusId;
		this.formatWorkflowTransitions();

		if (this.requestStatusBarLoading) {
			this.toggleRequestStatusBar(false);
		}

		this.checkPerformerUser();
		if (this.commentBlock) {
			this.commentBlock.loadComments(0, null);
		}

		this.updateFromCurrentClient = false;
		this.changeDetector.detectChanges();

		console.log(this.workflowTransitionsComponent.requestStatusId);
	}

	refreshRequestDeviceModels() {
		if (this.requestPageMainComponent != null) {
			this.requestPageMainComponent.loadRequestClaimDevices();
		}
	}

	private reloadActivities() {
		if (this.requestPageActivities) {
			this.requestPageActivities.loadActivities(this.entityIdInt);
		}
	}

	public onSelectedActivityChange() {
		if (this.selectedActivityId) {
			this.activitiesService.getById(this.selectedActivityId).subscribe(x => {
				this.selectedActivity = x.entity;
			});
		}
	}

	manualChangeStatus(statusToId: number) {
		this.updateFromCurrentClient = true;
		let transition = this.entityViewModel.entity.workflowTransitions.find(x => x.requestStatusToId == statusToId);
		
		if (transition != null) {
			this.workflowTransitionsComponent.changeStatus(transition.transitionId);
		}
		else {
			this.notificationService.error({ 
				title: 'Указанный переход не существует',
				notificationType: NotificationType.Toast 
			});
		}
	}

	startChangeStatus() {
		this.updateFromCurrentClient = true;
		this.toggleRequestStatusBar(true);
	}

	endChangeStatus(result: RequestChangeStatusResult) {
		this.updateFromCurrentClient = true;
		if (result && result.autoCallingWorkflowTransitionId) {
			if (result.recommendRouteRedistribution) {
				this.notificationService.info({
					title: 'Маршруты',
					message: 'По заявке были построенные маршруты. Рекомендуется перераспределить маршруты в планировании.',
					notificationType: NotificationType.SweetAlert
				});
			}
			this.requestsService.getWorkflowTransitions(this.entityIdInt).subscribe(resp => {
				this.entityViewModel.entity.workflowTransitions = resp;
				this.entityViewModel.entity.requestStatusId = result.currentRequestStatusId;
				this.entityViewModel.entity.requestStatusName = this.requestStatuses.find(x => 
					x.id == result.currentRequestStatusId).name;
				this.formatWorkflowTransitions();
				
				if (this.requestStatusBarLoading) {
					this.toggleRequestStatusBar(false);
				}

				this.workflowTransitionsComponent.requestStatusId = result.currentRequestStatusId;
				this.workflowTransitionsComponent.changeStatus(result.autoCallingWorkflowTransitionId);
				this.reloadActivities();
			});
		}
		else {
			this.requestsService.getBwtById(this.entityIdInt).subscribe(resp => {
				this.refreshRequest(resp.entity);
				this.reloadActivities();

				if (result && result.recommendRouteRedistribution) {
					this.notificationService.info({
						title: 'Маршруты',
						message: 'По заявке были построенные маршруты. Рекомендуется перераспределить маршруты в планировании.',
						notificationType: NotificationType.SweetAlert
					});
				}
				
				if (this.requestStatusBarLoading) {
					this.toggleRequestStatusBar(false);
				}
			});
		}
	}

	cancelChangeStatus() {
		this.toggleRequestStatusBar(false);
	}

	togglePrintFormsDropdown() {
		this.requestPrintFormsDropdown.noDataText = "Печатные формы не найдены";
		this.requestPrintFormsDropdown.toggle(!this.requestPrintFormsDropdown.isOpen);
	}

	generatePrintForm(printFormId: number) {
		let useToggleRequestStatusBar = !this.requestStatusBarLoading;
		if (useToggleRequestStatusBar) {
			this.toggleRequestStatusBar(true);
		}

		this.requestPrintFormsService.downloadRequestPrintFormFile(this.entityIdInt, printFormId).subscribe(blob => {
			const blobUrl = URL.createObjectURL(blob);
    		window.open(blobUrl, '_blank');
			this.requestPrintFormId = null;

			if (useToggleRequestStatusBar) {
				this.toggleRequestStatusBar(false);
			}
		});
	}

	private toggleRequestStatusBar(state: boolean) {
		let requestStatusBar = kendo.jQuery("#requestStatusBar");
		if (requestStatusBar) {
			kendo.ui.progress(requestStatusBar, state);
		}	
		this.requestStatusBarLoading = state;
	}

	get requestMainHeight(): number {
		if (this.requestPageMainElement != null) {
			return this.requestPageMainElement.nativeElement.offsetHeight;
		}

		return window.innerHeight / 2;
	}

	/**
	 * Высота чата:
	 * - при блоке «Связанные заявки» = высота только карточки «Основное»;
	 * - иначе — прежнее поведение (высота всей левой колонки / min 1000).
	 */
	get requestCommentsHeight(): number {
		void this.commentsHeightTick;

		if (this.relatedBlockVisible) {
			const mainCardHeight = this.getMainCardHeight();
			return mainCardHeight > 0 ? mainCardHeight : this.requestMainHeight;
		}

		if (this.defaultRequestCommentsHeight == null) {
			this.defaultRequestCommentsHeight = this.requestMainHeight >= this.minRequestCommentsHeight
				? this.requestMainHeight
				: this.minRequestCommentsHeight;
		}

		return this.defaultRequestCommentsHeight;
	}

	onRelatedVisibilityChange(visible: boolean): void {
		this.relatedBlockVisible = visible;
		setTimeout(() => this.syncRelatedLayout(), 0);
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		this.syncRelatedLayout();
	}

	private getMainCardHeight(): number {
		const mainCard = document.getElementById('request-main-card');
		if (mainCard) {
			return Math.round(mainCard.getBoundingClientRect().height);
		}
		return 0;
	}

	private recalculateCommentsHeight(): void {
		this.commentsHeightTick++;
		this.changeDetector.detectChanges();
	}

	/** Высота чата = «Основное»; верх «Связанные заявки» = верх первого виджета слева. */
	private syncRelatedLayout(): void {
		this.recalculateCommentsHeight();

		if (!this.relatedBlockVisible) {
			this.relatedTopMarginPx = 0;
			return;
		}

		// После применения высоты чата — выровнять верх related с первым виджетом
		requestAnimationFrame(() => {
			const firstWidget = document.getElementById('request-first-widget');
			const comments = document.getElementById('request-comments-card');
			if (!firstWidget || !comments) {
				this.relatedTopMarginPx = 0;
				this.changeDetector.detectChanges();
				return;
			}

			const widgetTop = firstWidget.getBoundingClientRect().top + window.scrollY;
			const commentsBottom = comments.getBoundingClientRect().bottom + window.scrollY;
			const gap = Math.round(widgetTop - commentsBottom);
			this.relatedTopMarginPx = Math.max(0, gap);
			this.changeDetector.detectChanges();
		});
	}

	private loadRelatedTree(requestId: number): void {
		if (!requestId) {
			this.relatedTree = null;
			this.relatedBlockVisible = this.isConsultationRequest();
			setTimeout(() => this.syncRelatedLayout(), 0);
			return;
		}

		this.requestsService.getRelatedTree(requestId).subscribe(
			tree => {
				this.relatedTree = tree && tree.root ? tree : null;
				this.relatedBlockVisible =
					this.isConsultationRequest()
					|| !!(this.relatedTree && this.relatedTree.root);
				setTimeout(() => this.syncRelatedLayout(), 0);
			},
			() => {
				this.relatedTree = null;
				this.relatedBlockVisible = this.isConsultationRequest();
				setTimeout(() => this.syncRelatedLayout(), 0);
			}
		);
	}

	private isConsultationRequest(): boolean {
		return !!(this.entityViewModel
			&& this.entityViewModel.entity
			&& this.entityViewModel.entity.requestTypeId === RequestType.consultation);
	}

	private getProcentValue(value: number, procent: number): number {
		return value / 100 * procent;
	}

	public statusIsCanceled(item: any): boolean {
		return item && item.statusId == <number>ActivityStatusEnum.cancelled;
	}

	hasChanges(): boolean {
		return false;
	}

	ngOnDestroy(): void {
		this.destroy = true;
    }

	get isPersonalWarehouseReplenishment(): boolean {
		return this.entityViewModel.entity.requestTypeId == <number>RequestType.personalWarehouseReplenishment;
	}

	get isServiceAgreement(): boolean {
		return this.entityViewModel.entity.requestTypeId == <number>RequestType.serviceAgreement;
	}

	/** Виджет анализов воды — Консультация (редактирование), Сервис и Монтаж (только просмотр) */
	get showWaterAnalysisWidget(): boolean {
		if (!this.entityViewModel || !this.entityViewModel.entity) {
			return false;
		}

		const requestTypeId = this.entityViewModel.entity.requestTypeId;
		return requestTypeId === <number>RequestType.consultation
			|| requestTypeId === <number>RequestType.service
			|| requestTypeId === <number>RequestType.install;
	}

	get canEditWaterAnalysis(): boolean {
		return this.isConsultationRequest() && this.canFullyEditRequest;
	}

	get isLaboratoryRequest(): boolean {
		return !!(this.entityViewModel
			&& this.entityViewModel.entity
			&& this.entityViewModel.entity.requestTypeId === <number>RequestType.laboratory);
	}

	get showMainTab(): boolean {
		return this.isSpecialRightClaimValid("Main") || this.securityService.isAdmin();
	}

	get showFilesTab(): boolean {
		return !this.isPersonalWarehouseReplenishment
			&& (this.isSpecialRightClaimValid("Files") || this.securityService.isAdmin())
			&& this.securityService.hasClaim('cat-requestfiles-read');
	}

	get showServicesTab(): boolean {
		return !this.isPersonalWarehouseReplenishment
			&& (this.isSpecialRightClaimValid("Services") || this.securityService.isAdmin());
	}

	get showCustomerObjectTab(): boolean {
		return !this.isPersonalWarehouseReplenishment
			&& (this.isSpecialRightClaimValid("CustomerObject") || this.securityService.isAdmin());
	}

	get showEquipmentTab(): boolean {
		return !this.isPersonalWarehouseReplenishment
			&& (this.isSpecialRightClaimValid("Equipment") || this.securityService.isAdmin());
	}

	get showServicesAndDevicesTab(): boolean {
		return this.showServicesTab || this.showEquipmentTab;
	}

	get showActivitiesTab(): boolean {
		return this.isSpecialRightClaimValid("Activities") || this.securityService.isAdmin();
	}

	get showContragentHistoryRequests(): boolean {
		return this.entityViewModel.entity.requestTypeKindId == <number>RequestTypeKind.Srv &&
			this.isSpecialRightClaimValid("ContragentHistoryRequests");
	}	

	get showNotificationsTab(): boolean {
		return !this.isPersonalWarehouseReplenishment
			&& (this.isSpecialRightClaimValid("Notifications") || this.securityService.isAdmin());
	}

	get showChangesHistoryTab(): boolean {
		return this.isSpecialRightClaimValid("ChangesHistory") || this.securityService.isAdmin();
	}

	get showWaterSamplingTab(): boolean {
		return this.entityViewModel.entity.requestTypeId == <number>RequestType.laboratory &&
			(this.isSpecialRightClaimValid("WaterSampling") || this.securityService.isAdmin());
	}

	get isEngineerRole(): boolean {
		return this.isSpecialRightClaimValid("Engineer");
	}

	get isMobileDevice(): boolean {
		return false;;
	}

	get selectedActivityId(): number {
		if (this.selectedActivities.length === 0) {
			return null;
		}

		return this.selectedActivities[0];
	}

	isSpecialRightClaimValid(claim: string): boolean {
		return this.securityService.isSpecialRightClaimValid(claim);
	}

	get canFullyEditRequest(): boolean {
		return !this.entityViewModel
			|| !this.entityViewModel.entity
			|| this.entityViewModel.entity.canFullyEditRequest !== false;
	}

	get canChangeRequestStatus(): boolean {
		return this.securityService.isCanChangeRequestStatus() && this.canFullyEditRequest;
	}
}
